import { useEffect, useState } from "react";
import { updateProfile } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { Link as LinkIcon, Mail } from "lucide-react";
import { db } from "../../firebase";
import { EducationFields } from "../../components/onboarding/fields";
import { type Education, educationDoc, educationFrom } from "../../utils/education";
import { MELT_INPUT, MELT_LABEL, MELT_REQUIRED } from "../../components/ui/melt";
import { cn } from "../../lib/utils";
import { SETTINGS_CARD, SaveRow, SettingsHeader, useSaveState, useSettings } from "./SettingsLayout";

type ProfileForm = { name: string; education: Education[]; linkedinUrl: string };

const str = (v: unknown) => (typeof v === "string" ? v : "");

const deleteMailto = (email: string | null) =>
  `mailto:help@oncaramel.com?subject=${encodeURIComponent("Delete my Caramel account")}&body=${encodeURIComponent(
    `Please delete my Caramel account${email ? ` (${email})` : ""}.`,
  )}`;

export default function ProfileSettings() {
  const { user, userRecord, userRecordLoading } = useSettings();
  const [form, setForm] = useState<ProfileForm | null>(null);
  const [initial, setInitial] = useState<ProfileForm | null>(null);
  const { saving, saved, error, run } = useSaveState();

  // Seed the form once the user doc arrives (not on every snapshot, which
  // would wipe unsaved edits).
  useEffect(() => {
    if (form || userRecordLoading || !user) return;
    const seeded = {
      name: user.displayName || str(userRecord?.name),
      education: educationFrom(userRecord),
      linkedinUrl: str(userRecord?.linkedinUrl),
    };
    setForm(seeded);
    setInitial(seeded);
  }, [form, user, userRecord, userRecordLoading]);

  if (!form || !user) {
    return <p className="font-plex text-xs text-melt-text3">Loading…</p>;
  }

  const set = (key: "name" | "linkedinUrl") => (value: string) => setForm({ ...form, [key]: value });
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = () =>
    run(async () => {
      const name = form.name.trim();
      if (name !== (user.displayName || "")) await updateProfile(user, { displayName: name });
      await setDoc(
        doc(db, "users", user.uid),
        {
          name,
          ...educationDoc(form.education),
          linkedinUrl: form.linkedinUrl.trim() || null,
        },
        { merge: true }
      );
      setInitial(form);
    });

  return (
    <>
      <SettingsHeader title="Profile" description="Your name and background. Caramel uses your school to find alumni at the companies you want." />

      <div className={cn(SETTINGS_CARD, "flex flex-col gap-5 p-6")}>
        <div className="flex flex-col gap-2">
          <label htmlFor="profile-name" className={MELT_LABEL}>Name<span aria-hidden className={MELT_REQUIRED}>*</span></label>
          <input
            id="profile-name"
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            placeholder="Your name"
            className={MELT_INPUT}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="profile-email" className={MELT_LABEL}>Email</label>
          <div className="relative">
            <Mail size={16} strokeWidth={1.75} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-melt-text3" />
            <input
              id="profile-email"
              value={user.email || ""}
              disabled
              className={cn(MELT_INPUT, "cursor-not-allowed bg-melt-paper pl-10 text-melt-text3")}
            />
          </div>
          <p className="text-xs text-melt-text3">Linked to your Google account, so it can't be changed.</p>
        </div>

        <EducationFields
          id="profile"
          value={form.education}
          onChange={(education) => setForm({ ...form, education })}
          labelClassName={MELT_LABEL}
        />

        <div className="flex flex-col gap-2">
          <label htmlFor="profile-linkedin" className={MELT_LABEL}>
            LinkedIn
          </label>
          <div className="relative">
            <LinkIcon size={17} strokeWidth={1.75} className="absolute left-[13px] top-[13px] text-melt-text3" />
            <input
              id="profile-linkedin"
              value={form.linkedinUrl}
              onChange={(e) => set("linkedinUrl")(e.target.value)}
              placeholder="linkedin.com/in/yourname"
              className={cn(MELT_INPUT, "pl-[38px]")}
            />
          </div>
        </div>

        <div className="border-t border-melt-hair pt-5">
          <SaveRow onSave={save} saving={saving} saved={saved} error={error} disabled={!dirty || !form.name.trim()} />
        </div>
      </div>

      <div className={cn(SETTINGS_CARD, "mt-4 p-6")}>
        <h2 className="text-[15px] font-semibold text-melt-text1">Delete your account</h2>
        <p className="mt-1 text-sm leading-relaxed text-melt-text2">
          This deletes your account and everything in it. Email{" "}
          <a href={deleteMailto(user.email)} className="font-medium text-melt-text1 underline underline-offset-2">
            help@oncaramel.com
          </a>{" "}
          from the address you signed in with, and we'll take care of it within 3 days.
        </p>
      </div>
    </>
  );
}
